import { useEffect, useState } from 'react';
import { LaborCategory, LaborRate } from '../../types/pmTypes';
import { getCurrentEffectiveRate } from '../../utils/companyResourceUtils';
import {
    listLaborCategoriesByCompanyId,
    listLaborRatesByCategoryId,
    updateLaborCategory,
} from '../../graphql/companyResourceQueries';
import { LaborRateModal } from './LaborRateModal';
import { DeleteLaborRateModal } from './DeleteLaborRateModal';
import { Btn, IconEdit, IconPlus, IconTrash } from '../ui';

interface LaborRatesTabProps {
    isAdmin: boolean;
    companyId: string;
}

export const LaborRatesTab = ({ isAdmin, companyId }: LaborRatesTabProps) => {
    const [categories, setCategories] = useState<LaborCategory[]>([]);
    const [ratesMap, setRatesMap] = useState<Record<string, LaborRate | null>>({});
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState<LaborCategory | null>(null);
    const [editingRate, setEditingRate] = useState<LaborRate | null>(null);
    const [deletingCategory, setDeletingCategory] = useState<LaborCategory | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const cats = await listLaborCategoriesByCompanyId(companyId);
            const activeCats = cats.filter((c: LaborCategory) => c.isActive);
            setCategories(activeCats);

            const rMap: Record<string, LaborRate | null> = {};
            for (const cat of activeCats) {
                const rates = await listLaborRatesByCategoryId(cat.id);
                rMap[cat.id] = getCurrentEffectiveRate(rates);
            }
            setRatesMap(rMap);
        } catch (err) {
            console.error('Error fetching labor rates:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) fetchData();
    }, [companyId]);

    const handleConfirmDelete = async () => {
        if (!deletingCategory) return;
        setIsDeleting(true);
        try {
            await updateLaborCategory({ id: deletingCategory.id, isActive: false });
            setCategories(prev => prev.filter(c => c.id !== deletingCategory.id));
            setDeletingCategory(null);
        } catch (err) {
            console.error('Error deactivating labor category:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSave = async () => {
        setShowModal(false);
        setEditingCategory(null);
        setEditingRate(null);
        await fetchData();
    };

    if (isLoading) {
        return <div className="cr-empty-state"><p className="cr-empty-state-text">Loading labor rates...</p></div>;
    }

    return (
        <div>
            <div className="cr-toolbar">
                {isAdmin && (
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Labor Rate"
                        buttonOnClick={() => { setEditingCategory(null); setEditingRate(null); setShowModal(true); }}
                    />
                )}
            </div>

            {categories.length === 0 ? (
                <div className="cr-empty-state">
                    <div className="cr-empty-state-icon">💲</div>
                    <p className="cr-empty-state-text">No labor rate categories yet. Add your first to get started.</p>
                </div>
            ) : (
                <table className="cr-table">
                    <thead>
                        <tr>
                            <th>Labor Type</th>
                            <th>ST Rate/HR</th>
                            <th>OT Rate/HR</th>
                            {isAdmin && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map(cat => {
                            const rate = ratesMap[cat.id];
                            return (
                                <tr key={cat.id}>
                                    <td>{cat.name}</td>
                                    <td>{rate ? `$${rate.standardRate.toFixed(2)}` : '—'}</td>
                                    <td>{rate ? `$${rate.overtimeRate.toFixed(2)}` : '—'}</td>
                                    {isAdmin && (
                                        <td className="cr-table-actions">
                                            <div className="table-actions">
                                                <Btn
                                                    variant="tertiary"
                                                    buttonSize="sm"
                                                    buttonClasses="btn-icon"
                                                    buttonIcon={<IconEdit />}
                                                    ariaLabel={`Edit ${cat.name}`}
                                                    buttonOnClick={() => { setEditingCategory(cat); setEditingRate(rate); setShowModal(true); }}
                                                />
                                                <Btn
                                                    variant="tertiary"
                                                    buttonSize="sm"
                                                    buttonClasses="btn-icon btn-icon-danger"
                                                    buttonIcon={<IconTrash />}
                                                    ariaLabel={`Remove ${cat.name}`}
                                                    buttonOnClick={() => setDeletingCategory(cat)}
                                                />
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {showModal && (
                <LaborRateModal
                    isOpen={showModal}
                    onClose={() => { setShowModal(false); setEditingCategory(null); setEditingRate(null); }}
                    onSave={handleSave}
                    editCategory={editingCategory}
                    currentRate={editingRate}
                    companyId={companyId}
                />
            )}

            <DeleteLaborRateModal
                category={deletingCategory}
                isDeleting={isDeleting}
                onDismiss={() => { if (!isDeleting) setDeletingCategory(null); }}
                onConfirmDelete={handleConfirmDelete}
            />
        </div>
    );
};
